/*
 * Collection-local age and range filters. Native Shopify section responses stay
 * authoritative for availability, colour, price, sorting and product-card HTML.
 * We fetch every native result page before applying independent local facets.
 * No cart, product, customer or checkout writes are performed by this adapter.
 */
(function () {
    'use strict';

    var ROOT_SELECTOR = '[data-ll-collection-facets]';
    var CUSTOM_PARAMS = ['ll_age', 'll_series', 'll_limit'];
    var MAX_PAGES = 20;
    var CACHE_LIMIT = 5;
    var PAGE_SIZES = [10, 15, 20, 25, 30, 50];
    var controller = null;

    function query(value) {
        return new URLSearchParams(value instanceof URLSearchParams ? value.toString() : (value || ''));
    }

    function positiveInteger(value, fallback) {
        var number = Number(value);
        return Number.isSafeInteger(number) && number > 0 ? number : fallback;
    }

    function nativeClass() {
        return window.customElements && window.customElements.get('collection-filters-form');
    }

    function nativeParams(value) {
        var params = query(value);
        CUSTOM_PARAMS.concat(['page', 'view', 'section_id']).forEach(function (name) { params.delete(name); });
        return params;
    }

    function cacheKey(params) {
        var ordered = query(params);
        ordered.sort();
        return ordered.toString();
    }

    function parseDocument(html) {
        return new DOMParser().parseFromString(html, 'text/html');
    }

    function serializeDocument(doc) {
        return '<!doctype html>' + doc.documentElement.outerHTML;
    }

    function failure(message) {
        var error = new Error(message);
        error.name = 'LenslottFacetError';
        return error;
    }

    function abortError() {
        var error = new Error('A newer filter request replaced this request.');
        error.name = 'AbortError';
        return error;
    }

    function filterProductId(card) {
        var node = card.querySelector('[data-product-id]');
        return node && String(node.getAttribute('data-product-id'));
    }

    function labelsFor(input) {
        var labels = input.labels ? Array.from(input.labels) : [];
        if (!labels.length && input.id) {
            labels = Array.from(document.querySelectorAll('label')).filter(function (label) { return label.htmlFor === input.id; });
        }
        return labels;
    }

    function FacetController(root) {
        this.root = root;
        this.controls = root.querySelector('[data-ll-controls]');
        this.legacy = root.querySelector('[data-ll-legacy-categories]');
        this.status = root.querySelector('[data-ll-facet-status]');
        this.retry = root.querySelector('[data-ll-retry]');
        this.engine = window.LenslottFacetEngine;
        this.collection = root.dataset.collection;
        this.sectionId = root.dataset.sectionId;
        this.defaultPerPage = positiveInteger(root.dataset.perPage, 20);
        this.paginationMode = root.dataset.paginationMode === 'show_more' ? 'show_more' : 'pagination';
        this.loadMoreTemplate = root.querySelector('template[data-ll-load-more-template]');
        var initialSort = document.querySelector('#CollectionFiltersForm [name="sort_by"]');
        this.defaultSort = root.dataset.defaultSort || (!query(window.location.search).has('sort_by') && initialSort ? initialSort.value : null);
        this.ready = false;
        this.initializing = false;
        this.queuedRender = null;
        this.metadata = null;
        this.metadataPromise = null;
        this.cache = new Map();
        this.sequence = 0;
        this.abortController = null;
        this.lastParams = query(window.location.search);
        this.pendingParams = null;
        this.appliedFilterHTML = null;
        this.lastRetry = null;
        this.labelByValue = new Map();
        this.boundClick = this.onClick.bind(this);
        this.boundChange = this.onChange.bind(this);

        root.querySelectorAll('[data-ll-facet]').forEach(function (input) {
            var label = input.closest('label');
            var copy = label ? label.cloneNode(true) : null;
            if (copy) copy.querySelectorAll('input, [data-ll-count]').forEach(function (node) { node.remove(); });
            this.labelByValue.set(input.dataset.group + ':' + input.value, copy ? copy.textContent.trim() : input.value);
        }, this);

        document.addEventListener('click', this.boundClick, true);
        document.addEventListener('change', this.boundChange, true);
        this.initialize();
    }

    FacetController.prototype.setStatus = function (message, isError, visuallyHidden) {
        if (this.status) {
            this.status.classList.toggle('visually-hidden', !!visuallyHidden && !isError);
            this.status.textContent = message;
            this.status.dataset.error = isError ? 'true' : 'false';
        }
        if (this.retry) this.retry.hidden = !isError;
    };

    FacetController.prototype.setReady = function (ready) {
        this.ready = ready;
        this.root.dataset.llReady = ready ? 'true' : 'false';
        if (this.controls) this.controls.hidden = !ready;
        if (this.legacy) this.legacy.hidden = ready;
        if (!ready) this.root.querySelectorAll('[data-ll-facet]').forEach(function (input) { input.disabled = true; });
    };

    FacetController.prototype.setBusy = function (busy) {
        this.root.setAttribute('aria-busy', busy ? 'true' : 'false');
        if (busy) document.body.classList.add('has-halo-loader');
        else document.body.classList.remove('has-halo-loader');
    };

    FacetController.prototype.fetchText = async function (url, signal) {
        var response = await fetch(url.toString(), { signal: signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw failure('The collection request returned HTTP ' + response.status + '.');
        if (response.url && new URL(response.url, window.location.origin).pathname !== window.location.pathname) {
            throw failure('The collection response redirected to a different page.');
        }
        return response.text();
    };

    FacetController.prototype.checkSupportedFilters = function (filters, params) {
        if (!this.engine || !this.engine.parseState || !this.engine.countOptions || !this.engine.matchesNative) {
            throw failure('The collection filter engine is unavailable.');
        }
        var unsupported = this.engine.unsupportedNativeParams(params || '');
        var supported = this.engine.supportedNativeParams;
        (filters || []).forEach(function (filter) {
            var names = filter.type === 'price_range' ? [filter.min_value && filter.min_value.param_name, filter.max_value && filter.max_value.param_name] : [filter.param_name];
            names.filter(Boolean).forEach(function (name) {
                if (supported.indexOf(name) === -1) unsupported.push(name);
            });
        });
        if (unsupported.length) throw failure('An unsupported collection filter needs review: ' + Array.from(new Set(unsupported)).join(', ') + '.');
    };

    FacetController.prototype.loadMetadata = function () {
        if (this.metadata) return Promise.resolve(this.metadata);
        if (this.metadataPromise) return this.metadataPromise;
        this.metadataPromise = this.fetchMetadata().then(function (metadata) {
            this.metadata = metadata;
            return metadata;
        }.bind(this)).catch(function (error) {
            this.metadataPromise = null;
            throw error;
        }.bind(this));
        return this.metadataPromise;
    };

    FacetController.prototype.fetchMetadata = async function () {
        var url = new URL(window.location.href);
        Array.from(url.searchParams.keys()).forEach(function (name) {
            if (name.indexOf('filter.') === 0 || CUSTOM_PARAMS.concat(['page', 'sort_by', 'section_id', 'view']).indexOf(name) !== -1) url.searchParams.delete(name);
        });
        url.searchParams.set('view', 'lenslott-facets');
        var products = [];
        var ids = new Set();
        var excluded = 0;
        var first = null;
        var next = 1;
        while (next) {
            if (next > MAX_PAGES) throw failure('This collection is too large for the verified local filter limit.');
            url.searchParams.set('page', String(next));
            var doc = parseDocument(await this.fetchText(url));
            var script = doc.querySelector('script[data-ll-facet-data]');
            if (!script) throw failure('The full collection data view was not returned.');
            var page = JSON.parse(script.textContent);
            if (page.handle !== this.collection || Number(page.page) !== next || !Array.isArray(page.products)) {
                throw failure('The collection metadata identity or page is invalid.');
            }
            if (!first) {
                first = page;
                this.checkSupportedFilters(page.filters, window.location.search);
                if (Number(page.pages) > MAX_PAGES || Number(page.filtered_count) !== Number(page.all_count)) {
                    throw failure('The full unfiltered collection could not be verified.');
                }
            } else if (Number(page.all_count) !== Number(first.all_count) || Number(page.pages) !== Number(first.pages)) {
                throw failure('The collection changed while its metadata was loading.');
            }
            page.products.forEach(function (product) {
                var id = String(product.id);
                if (!id || ids.has(id) || !Array.isArray(product.collections) || !Array.isArray(product.variants)) throw failure('The collection contains incomplete or duplicate product data.');
                ids.add(id);
                products.push(product);
            });
            if (Number(page.visible_page_count) !== page.products.length) throw failure('The metadata product count is inconsistent.');
            excluded += Number(page.excluded_page_count) || 0;
            next = page.next_page === null ? null : positiveInteger(page.next_page, -1);
            if (next !== null && next !== Number(page.page) + 1) throw failure('The metadata pagination is incomplete.');
            if ((next === null) !== (Number(page.page) === Number(first.pages))) throw failure('The metadata final page is inconsistent.');
        }
        if (products.length + excluded !== Number(first.filtered_count)) throw failure('Not every collection product was returned.');
        return { products: products, byId: new Map(products.map(function (product) { return [String(product.id), product]; })), filters: first.filters || [], excluded: excluded };
    };

    FacetController.prototype.initialize = async function () {
        if (this.initializing) return;
        this.initializing = true;
        this.setReady(false);
        this.setStatus('', false);
        try {
            if (!this.collection || !this.sectionId || !nativeClass()) throw failure('The collection filter integration is unavailable.');
            await this.loadMetadata();
            // The server has not applied ll_* filters on a direct/deep URL yet.
            // Keep a truthful native-only rollback state until the adapter has
            // fetched and committed that URL's complete filtered result.
            this.lastParams = query(window.location.search);
            CUSTOM_PARAMS.forEach(function (name) { this.lastParams.delete(name); }, this);
            var requested = this.queuedRender;
            this.queuedRender = null;
            this.setReady(true);
            this.syncUI(this.lastParams);
            this.appliedFilterHTML = document.querySelector('collection-filters-form').outerHTML;
            this.setStatus('', false);
            var directParams = this.normalizedParams(window.location.search);
            if (requested) {
                this.render(requested.searchParams, requested.event, requested.updateHistory);
            } else if (this.hasCustom(directParams) || directParams.has('ll_limit') || (this.paginationMode === 'show_more' && positiveInteger(directParams.get('page'), 1) > 1)) {
                this.syncUI(directParams);
                this.render(directParams, null, false);
            }
        } catch (error) {
            this.setReady(false);
            this.lastRetry = null;
            this.setStatus('Age and collection filters could not load. Existing categories and standard filters remain available. Please retry.', true);
            // Do not swallow a standard-filter interaction made while the
            // metadata request was pending. Replay it through the original
            // native path after the adapter has entered explicit fallback.
            var fallback = this.queuedRender;
            this.queuedRender = null;
            if (fallback && nativeClass()) {
                setTimeout(function () {
                    var params = query(fallback.searchParams);
                    CUSTOM_PARAMS.forEach(function (name) { params.delete(name); });
                    nativeClass().renderPage(params.toString(), fallback.event, fallback.updateHistory);
                }, 0);
            }
        } finally {
            this.initializing = false;
        }
    };

    FacetController.prototype.hasCustom = function (params) {
        var state = this.engine.parseState(params);
        return state.ages.length > 0 || state.series.length > 0;
    };

    FacetController.prototype.normalizedParams = function (input, event) {
        var params = query(input);
        var current = this.pendingParams ? query(this.pendingParams) : query(window.location.search);
        current.forEach(function (value, name) {
            if (!params.has(name) && name.indexOf('filter.') !== 0 && CUSTOM_PARAMS.concat(['page', 'sort_by', 'view', 'section_id']).indexOf(name) === -1) params.append(name, value);
        });
        if (event) {
            CUSTOM_PARAMS.forEach(function (name) {
                if (!params.has(name) && current.has(name)) params.set(name, current.get(name));
            });
        }
        var state = this.engine.parseState(params);
        [['ll_age', state.ages], ['ll_series', state.series]].forEach(function (pair) {
            if (pair[1].length) params.set(pair[0], pair[1].join(','));
            else params.delete(pair[0]);
        });
        if (params.has('ll_limit') && PAGE_SIZES.indexOf(Number(params.get('ll_limit'))) === -1) params.delete('ll_limit');
        if (params.has('page')) params.set('page', String(positiveInteger(params.get('page'), 1)));
        params.delete('view');
        params.delete('section_id');
        return params;
    };

    FacetController.prototype.groups = function () {
        var groups = { ages: [], series: [], availability: [], colors: [] };
        this.root.querySelectorAll('[data-ll-facet]').forEach(function (input) {
            groups[input.dataset.group === 'age' ? 'ages' : 'series'].push(input.value);
        });
        (this.metadata.filters || []).forEach(function (filter) {
            var key = filter.param_name === 'filter.v.availability' ? 'availability' : filter.param_name === 'filter.v.option.color' ? 'colors' : null;
            if (key) groups[key] = (filter.values || []).map(function (value) { return String(value.value); });
        });
        return groups;
    };

    FacetController.prototype.syncHiddenInputs = function (params) {
        var form = document.getElementById('CollectionFiltersForm');
        if (!form) return;
        form.querySelectorAll('input[data-ll-state]').forEach(function (input) { input.remove(); });
        CUSTOM_PARAMS.forEach(function (name) {
            if (!params.has(name)) return;
            var input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = params.get(name);
            input.setAttribute('data-ll-state', '');
            form.appendChild(input);
        });
    };

    FacetController.prototype.syncUI = function (params) {
        var state = this.engine.parseState(params);
        var counts = this.engine.countOptions(this.metadata.products, state, this.groups());
        this.root.querySelectorAll('[data-ll-facet]').forEach(function (input) {
            var group = input.dataset.group === 'age' ? 'ages' : 'series';
            var count = counts[group][input.value] || 0;
            input.checked = state[group].indexOf(input.value) !== -1;
            input.disabled = !input.checked && count === 0;
            var label = input.closest('label');
            if (label) {
                var countNode = label.querySelector('[data-ll-count]');
                if (countNode) countNode.textContent = String(count);
                label.classList.toggle('is-disabled', input.disabled);
            }
        });
        var form = document.getElementById('CollectionFiltersForm');
        if (form) {
            form.querySelectorAll('input[type="checkbox"][name="filter.v.availability"], input[type="checkbox"][name="filter.v.option.color"]').forEach(function (input) {
                var group = input.name === 'filter.v.availability' ? 'availability' : 'colors';
                var count = counts[group][input.value] || 0;
                input.checked = state[group].indexOf(input.value) !== -1;
                input.disabled = !input.checked && count === 0;
                labelsFor(input).forEach(function (label) {
                    var counter = label.querySelector('.count');
                    if (counter) counter.textContent = '(' + count + ')';
                    label.classList.toggle('facet-checkbox--disabled', input.disabled);
                });
            });
        }
        this.syncHiddenInputs(params);
        this.renderChips(state);
        this.syncToolbar(params);
    };

    FacetController.prototype.renderChips = function (state) {
        var container = this.root.querySelector('[data-ll-selected]');
        if (!container) return;
        var clear = this.root.querySelector('[data-ll-clear]');
        var clearWasInside = !!clear && container.contains(clear);
        container.replaceChildren();
        [['age', state.ages], ['series', state.series]].forEach(function (pair) {
            pair[1].forEach(function (value) {
                var chip = document.createElement('button');
                chip.type = 'button';
                chip.className = 'll-facet-chip';
                chip.dataset.llRemoveGroup = pair[0];
                chip.dataset.llRemoveValue = value;
                var label = this.labelByValue.get(pair[0] + ':' + value) || value;
                chip.textContent = label + ' ×';
                chip.setAttribute('aria-label', 'Remove ' + label + ' filter');
                container.appendChild(chip);
            }, this);
        }, this);
        var hasFilters = state.ages.length || state.series.length || state.colors.length || state.availability.length || state.min !== null || state.max !== null;
        if (clearWasInside) container.appendChild(clear);
        if (clear) clear.hidden = !hasFilters;
        container.hidden = !(state.ages.length || state.series.length || (clearWasInside && hasFilters));
    };

    FacetController.prototype.syncToolbar = function (params) {
        var perPage = this.perPage(params);
        document.querySelectorAll('[data-limited-view]').forEach(function (toolbar) {
            var text = toolbar.querySelector('.label-text');
            if (text) text.textContent = String(perPage);
            toolbar.querySelectorAll('[data-limited-view-item]').forEach(function (item) {
                var value = item.querySelector('[data-value]');
                item.classList.toggle('is-active', !!value && Number(value.dataset.value) === perPage);
            });
        });
        var sort = params.get('sort_by') || this.defaultSort;
        if (sort) {
            document.querySelectorAll('#CollectionFiltersForm [name="sort_by"]').forEach(function (input) { input.value = sort; });
            document.querySelectorAll('[data-sorting]').forEach(function (toolbar) {
                toolbar.querySelectorAll('[data-sort-by-item]').forEach(function (item) {
                    var node = item.querySelector('[data-value]');
                    var active = !!node && node.dataset.value === sort;
                    item.classList.toggle('is-active', active);
                    var text = toolbar.querySelector('.label-text');
                    if (active && text) text.textContent = node.textContent;
                });
            });
        }
    };

    FacetController.prototype.perPage = function (params) {
        var requested = Number(params.get('ll_limit'));
        return PAGE_SIZES.indexOf(requested) !== -1 ? requested : this.defaultPerPage;
    };

    FacetController.prototype.loadNativeCards = async function (params, signal) {
        var native = nativeParams(params);
        this.checkSupportedFilters(this.metadata.filters, native);
        var key = cacheKey(native);
        if (this.cache.has(key)) {
            var cached = this.cache.get(key);
            this.cache.delete(key);
            this.cache.set(key, cached);
            return cached;
        }
        var state = this.engine.parseState(native);
        var expected = new Set(this.metadata.products.filter(function (product) { return this.engine.matchesNative(product, state); }, this).map(function (product) { return String(product.id); }));
        var url = new URL(window.location.href);
        url.search = native.toString();
        url.searchParams.set('section_id', this.sectionId);
        var cards = [];
        var seen = new Set();
        var firstHTML = null;
        var maxPage = 1;
        for (var pageNumber = 1; pageNumber <= maxPage; pageNumber += 1) {
            if (pageNumber > MAX_PAGES) throw failure('The collection result exceeds the verified pagination limit.');
            url.searchParams.set('page', String(pageNumber));
            var html = await this.fetchText(url, signal);
            var doc = parseDocument(html);
            var region = doc.querySelector('#CollectionProductGrid .collection');
            if (!region) throw failure('The native collection result is missing.');
            if (!firstHTML) firstHTML = html;
            var list = region.querySelector('.productListing');
            var items = list ? Array.from(list.children).filter(function (node) { return node.classList.contains('product'); }) : [];
            items.forEach(function (item) {
                var id = filterProductId(item);
                if (!id || seen.has(id) || !this.metadata.byId.has(id) || !expected.has(id)) throw failure('A native result could not be verified against the full collection.');
                seen.add(id);
                cards.push({ id: id, html: item.outerHTML });
            }, this);
            region.querySelectorAll('.pagination a[href]').forEach(function (anchor) {
                var href = (anchor.getAttribute('href') || '').trim();
                if (!href || href.charAt(0) === '#' || /^javascript:/i.test(href) || anchor.classList.contains('disabled') || anchor.hasAttribute('disabled') || anchor.getAttribute('aria-disabled') === 'true') return;
                var nextURL = new URL(href, url);
                if (nextURL.origin !== url.origin || nextURL.pathname !== url.pathname) throw failure('The collection pagination left the current collection.');
                if (!nextURL.searchParams.has('page')) return;
                var nextPage = positiveInteger(nextURL.searchParams.get('page'), 0);
                if (!nextPage) throw failure('The collection pagination contains an invalid page.');
                maxPage = Math.max(maxPage, nextPage);
            });
            if (maxPage > MAX_PAGES) throw failure('The collection result exceeds the verified pagination limit.');
            if (signal.aborted) throw abortError();
        }
        if (seen.size !== expected.size || Array.from(expected).some(function (id) { return !seen.has(id); })) {
            throw failure('Not every matching collection product was returned.');
        }
        var result = { firstHTML: firstHTML, cards: cards };
        this.cache.set(key, result);
        while (this.cache.size > CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
        return result;
    };

    FacetController.prototype.makePagination = function (doc, total, page, perPage, params) {
        var pageCount = Math.ceil(total / perPage);
        if (pageCount <= 1) return null;
        if (this.paginationMode === 'show_more') {
            if (!this.loadMoreTemplate || !this.loadMoreTemplate.content.firstElementChild) throw failure('The collection load-more template is unavailable.');
            var loadMore = doc.importNode(this.loadMoreTemplate.content.firstElementChild, true);
            var shown = Math.min(total, page * perPage);
            loadMore.querySelector('[data-total-start]').textContent = '1';
            loadMore.querySelector('[data-total-end]').textContent = String(shown);
            var totalLabel = loadMore.querySelector('[data-ll-total-label]');
            totalLabel.textContent = totalLabel.textContent.replace('__LL_TOTAL__', String(total));
            loadMore.querySelector('.pagination-total-item').style.width = String(shown * 100 / total) + '%';
            var button = loadMore.querySelector('.pagination-page-infinite a');
            if (page < pageCount) {
                var nextParams = query(params);
                nextParams.set('page', String(page + 1));
                var href = window.location.pathname + '?' + nextParams.toString();
                button.setAttribute('href', href);
                button.setAttribute('data-href', href);
                button.setAttribute('data-infinite-scrolling', '');
                button.dataset.llPage = String(page + 1);
                button.textContent = button.dataset.loadMore;
            } else {
                button.classList.add('disabled');
                button.setAttribute('aria-disabled', 'true');
                button.setAttribute('tabindex', '-1');
                button.removeAttribute('href');
                button.textContent = button.dataset.noMore;
            }
            return loadMore;
        }
        var wrapper = doc.createElement('div');
        wrapper.className = 'pagination-wrapper text-center';
        var nav = doc.createElement('nav');
        nav.className = 'pagination ll-facet-pagination';
        nav.setAttribute('aria-label', 'Collection results pages');
        var list = doc.createElement('ul');
        list.className = 'pagination__list list-unstyled';
        var addLink = function (number, text, current) {
            var li = doc.createElement('li');
            li.className = 'pagination-num';
            var node = doc.createElement(current ? 'span' : 'a');
            node.className = 'pagination__item' + (current ? ' pagination__item--current' : ' link');
            node.textContent = text;
            if (current) node.setAttribute('aria-current', 'page');
            else {
                var pageParams = query(params);
                pageParams.set('page', String(number));
                node.setAttribute('href', window.location.pathname + '?' + pageParams.toString());
                node.dataset.llPage = String(number);
                node.setAttribute('aria-label', 'Page ' + number);
            }
            li.appendChild(node);
            list.appendChild(li);
        };
        if (page > 1) addLink(page - 1, 'Previous', false);
        for (var number = 1; number <= pageCount; number += 1) addLink(number, String(number), number === page);
        if (page < pageCount) addLink(page + 1, 'Next', false);
        nav.appendChild(list);
        wrapper.appendChild(nav);
        return wrapper;
    };

    FacetController.prototype.buildResult = function (record, params) {
        var doc = parseDocument(record.firstHTML);
        var region = doc.querySelector('#CollectionProductGrid .collection');
        var prototype = region.querySelector('.productListing') || document.querySelector('#CollectionProductGrid .productListing');
        var grid = prototype ? prototype.cloneNode(false) : doc.createElement('ul');
        grid.classList.add('productListing', 'list-unstyled');
        if (!prototype) grid.classList.add('productGrid', 'column-4');
        grid.id = 'main-collection-product-grid';
        grid.dataset.id = this.sectionId;
        var state = this.engine.parseState(params);
        var selected = record.cards.filter(function (card) { return this.engine.matchesCustom(this.metadata.byId.get(card.id), state); }, this);
        var perPage = this.perPage(params);
        var pages = Math.max(1, Math.ceil(selected.length / perPage));
        var page = Math.min(positiveInteger(params.get('page'), 1), pages);
        if (page > 1) params.set('page', String(page));
        else params.delete('page');
        // Show more is cumulative, including restored/deep-linked states. Only
        // an explicitly numbered pager replaces one page with another.
        var visible = selected.slice(this.paginationMode === 'show_more' ? 0 : (page - 1) * perPage, page * perPage);
        visible.forEach(function (card) {
            var template = doc.createElement('template');
            template.innerHTML = card.html;
            grid.appendChild(template.content.firstElementChild);
        });
        region.replaceChildren(grid);
        region.classList.toggle('collection--empty', selected.length === 0);
        region.removeAttribute('id');
        region.removeAttribute('data-id');
        if (!selected.length) {
            var empty = doc.createElement('div');
            empty.className = 'collection-product-wrapper ll-facet-empty';
            empty.setAttribute('role', 'status');
            var message = doc.createElement('p');
            message.textContent = 'No products match these filters in this collection.';
            var clear = doc.createElement('button');
            clear.type = 'button';
            clear.className = 'button button--primary';
            clear.setAttribute('data-ll-clear-results', '');
            clear.textContent = 'Clear filters';
            empty.append(message, clear);
            region.appendChild(empty);
        }
        var pagination = this.makePagination(doc, selected.length, page, perPage, params);
        if (pagination) region.appendChild(pagination);
        doc.querySelectorAll('#CollectionProductGrid .results-count .count').forEach(function (node) { node.textContent = String(selected.length); });
        return { html: serializeDocument(doc), total: selected.length, page: page, perPage: perPage };
    };

    FacetController.prototype.restoreNativeChips = function (source) {
        var fetched = parseDocument(source).querySelector('#CollectionFiltersForm .refined-widgets');
        var current = document.querySelector('#CollectionFiltersForm .refined-widgets');
        if (fetched && current) {
            current.innerHTML = fetched.innerHTML;
            current.style.display = current.querySelector('li') ? 'block' : 'none';
        }
    };

    FacetController.prototype.writeHistory = function (params, push) {
        var search = params.toString();
        var destination = window.location.pathname + (search ? '?' + search : '') + window.location.hash;
        var current = window.location.pathname + window.location.search + window.location.hash;
        if (destination === current) return;
        var state = { searchParams: search, lenslottFacets: true };
        if (push) history.pushState(state, '', destination);
        else history.replaceState(state, '', destination);
    };

    FacetController.prototype.render = async function (input, event, updateHistory) {
        var params = this.normalizedParams(input, event);
        var sequence = ++this.sequence;
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        var signal = this.abortController.signal;
        this.lastRetry = { params: params.toString(), event: null, updateHistory: updateHistory !== false };
        this.pendingParams = query(params);
        this.syncHiddenInputs(params);
        this.setBusy(true);
        this.setStatus('Updating collection results…', false);
        var gridSnapshot = null;
        var filtersSnapshot = null;
        var appliedParams = query(this.lastParams);
        var appliedFilterHTML = this.appliedFilterHTML;
        try {
            await this.loadMetadata();
            this.checkSupportedFilters(this.metadata.filters, params);
            var record = await this.loadNativeCards(params, signal);
            if (sequence !== this.sequence || signal.aborted) throw abortError();
            var result = this.buildResult(record, params);
            var native = nativeClass();
            var liveRegion = document.querySelector('#CollectionProductGrid .collection');
            var filterElement = document.querySelector('collection-filters-form');
            if (!native || !liveRegion || !filterElement) throw failure('The collection controls changed before the result was applied.');
            gridSnapshot = { element: liveRegion, html: liveRegion.innerHTML, className: liveRegion.className, id: liveRegion.getAttribute('id'), dataId: liveRegion.getAttribute('data-id') };
            filtersSnapshot = { element: filterElement, html: filterElement.outerHTML };
            native.renderFilters(record.firstHTML, event);
            this.restoreNativeChips(record.firstHTML);
            // Native empty results put the grid ID on the outer div. Our result
            // always restores a real UL, which must be the only grid with it.
            if (liveRegion.id === 'main-collection-product-grid') {
                liveRegion.removeAttribute('id');
                liveRegion.removeAttribute('data-id');
            }
            native.renderProductGrid(result.html, this.paginationMode === 'show_more' && result.page > 1);
            document.querySelector('#CollectionProductGrid .collection').classList.toggle('collection--empty', result.total === 0);
            this.syncUI(params);
            document.querySelectorAll('.results-count .count').forEach(function (node) { node.textContent = String(result.total); });
            this.writeHistory(params, updateHistory !== false);
            this.lastParams = query(params);
            this.appliedFilterHTML = document.querySelector('collection-filters-form').outerHTML;
            native.searchParamsPrev = params.toString();
            this.lastRetry = null;
            this.setStatus(result.total + (result.total === 1 ? ' product matches' : ' products match') + ' in this collection.', false, true);
            document.dispatchEvent(new CustomEvent('lenslott:collection-facets:updated', { detail: { total: result.total, page: result.page, perPage: result.perPage } }));
        } catch (error) {
            if (sequence !== this.sequence || error.name === 'AbortError') return;
            if (gridSnapshot) {
                gridSnapshot.element.innerHTML = gridSnapshot.html;
                gridSnapshot.element.className = gridSnapshot.className;
                if (gridSnapshot.id !== null) gridSnapshot.element.setAttribute('id', gridSnapshot.id);
                else gridSnapshot.element.removeAttribute('id');
                if (gridSnapshot.dataId !== null) gridSnapshot.element.setAttribute('data-id', gridSnapshot.dataId);
                else gridSnapshot.element.removeAttribute('data-id');
            }
            var currentFilters = document.querySelector('collection-filters-form');
            var restoreHTML = appliedFilterHTML || (filtersSnapshot && filtersSnapshot.html);
            if (currentFilters && restoreHTML) {
                var restored = parseDocument(restoreHTML).querySelector('collection-filters-form');
                currentFilters.replaceWith(document.importNode(restored, true));
            }
            this.lastParams = appliedParams;
            this.appliedFilterHTML = appliedFilterHTML;
            this.syncUI(appliedParams);
            // Deep links and popstate have already changed the address bar.
            // Roll those entries back too if their result could not be applied.
            this.writeHistory(appliedParams, false);
            if (nativeClass()) nativeClass().searchParamsPrev = appliedParams.toString();
            this.setStatus('The filter update could not be completed. Your previous results have been kept. Please retry.', true);
        } finally {
            if (sequence === this.sequence) {
                this.pendingParams = null;
                this.setBusy(false);
            }
        }
    };

    FacetController.prototype.clearParams = function () {
        var params = this.pendingParams ? query(this.pendingParams) : query(window.location.search);
        Array.from(params.keys()).forEach(function (name) {
            if (name.indexOf('filter.') === 0 || name === 'll_age' || name === 'll_series' || name === 'page') params.delete(name);
        });
        return params;
    };

    FacetController.prototype.nativeRemovalParams = function (link, removalURL) {
        var desired = this.pendingParams ? query(this.pendingParams) : query(window.location.search);
        var baseline = nativeParams(this.lastParams);
        var destination = nativeParams(removalURL.search);
        var groupBlock = link.classList.contains('facets__reset') ? link.closest('.js-filter') : null;
        if (groupBlock) {
            var names = new Set(Array.from(groupBlock.querySelectorAll('input[name]')).map(function (input) { return input.name; }).filter(function (name) { return name.indexOf('filter.') === 0; }));
            names.forEach(function (name) { desired.delete(name); });
            if (names.size) {
                desired.delete('page');
                return desired;
            }
        }
        // Server-built remove links describe the last applied state, not a
        // newer in-flight selection. Apply only their removal delta so clearing
        // a price chip cannot silently discard a pending colour or age change.
        var names = new Set(Array.from(baseline.keys()).concat(Array.from(destination.keys())).filter(function (name) { return name.indexOf('filter.') === 0; }));
        names.forEach(function (name) {
            var oldValues = baseline.getAll(name);
            var keptValues = destination.getAll(name);
            var removed = oldValues.filter(function (value) { return keptValues.indexOf(value) === -1; });
            if (!removed.length) return;
            if (name === 'filter.v.price.gte' || name === 'filter.v.price.lte') {
                desired.delete(name);
                return;
            }
            var nextValues = desired.getAll(name).filter(function (value) { return removed.indexOf(value) === -1; });
            desired.delete(name);
            nextValues.forEach(function (value) { desired.append(name, value); });
        });
        desired.delete('page');
        return desired;
    };

    FacetController.prototype.onChange = function (event) {
        if (!this.ready || !event.target.matches('[data-ll-facet]') || !this.root.contains(event.target)) return;
        event.stopImmediatePropagation();
        var params = this.pendingParams ? query(this.pendingParams) : query(window.location.search);
        ['age', 'series'].forEach(function (group) {
            var values = Array.from(this.root.querySelectorAll('[data-ll-facet][data-group="' + group + '"]:checked')).map(function (input) { return input.value; });
            var name = group === 'age' ? 'll_age' : 'll_series';
            if (values.length) params.set(name, values.join(','));
            else params.delete(name);
        }, this);
        params.delete('page');
        this.render(params, null, true);
    };

    FacetController.prototype.onClick = function (event) {
        var target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        var retry = target.closest('[data-ll-retry]');
        if (retry && this.root.contains(retry)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (!this.ready) this.initialize();
            else if (this.lastRetry) this.render(this.lastRetry.params, null, this.lastRetry.updateHistory);
            return;
        }
        if (!this.ready) return;
        var chip = target.closest('[data-ll-remove-group]');
        if (chip && this.root.contains(chip)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            var params = this.pendingParams ? query(this.pendingParams) : query(window.location.search);
            var name = chip.dataset.llRemoveGroup === 'age' ? 'll_age' : 'll_series';
            var values = (params.get(name) || '').split(',').filter(function (value) { return value && value !== chip.dataset.llRemoveValue; });
            if (values.length) params.set(name, values.join(','));
            else params.delete(name);
            params.delete('page');
            this.render(params, null, true);
            return;
        }
        var clear = target.closest('[data-ll-clear], [data-ll-clear-results], #CollectionFiltersForm .facets__clear-all');
        if (clear && (this.root.contains(clear) || clear.closest('#CollectionProductGrid, #CollectionFiltersForm'))) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.render(this.clearParams(), null, true);
            return;
        }
        var nativeRemove = target.closest('#CollectionFiltersForm .js-facet-remove[href]');
        if (nativeRemove && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
            var removeURL = new URL(nativeRemove.getAttribute('href'), window.location.href);
            if (removeURL.origin !== window.location.origin || removeURL.pathname !== window.location.pathname) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            var removeParams = this.nativeRemovalParams(nativeRemove, removeURL);
            this.render(removeParams, null, true);
            return;
        }
        var pageLink = target.closest('#CollectionProductGrid .pagination a[href], #CollectionProductGrid [data-ll-page]');
        if (pageLink && this.paginationMode === 'show_more' && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0)) {
            var navigationURL = new URL(pageLink.getAttribute('href'), window.location.href);
            if (navigationURL.origin === window.location.origin && navigationURL.pathname === window.location.pathname) {
                // Keep the real link's open-in-new-tab/window behavior without
                // letting the theme's competing AJAX append handler consume it.
                event.stopImmediatePropagation();
            }
            return;
        }
        if (pageLink && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
            var url = new URL(pageLink.getAttribute('href'), window.location.href);
            if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            var pageParams = this.pendingParams ? query(this.pendingParams) : query(window.location.search);
            pageParams.set('page', String(positiveInteger(url.searchParams.get('page'), 1)));
            this.render(pageParams, null, true);
            return;
        }
        var limit = target.closest('[data-limited-view-item]');
        if (limit && limit.closest('#CollectionProductGrid')) {
            var value = limit.querySelector('[data-value]');
            if (!value || PAGE_SIZES.indexOf(Number(value.dataset.value)) === -1) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            var limitParams = this.pendingParams ? query(this.pendingParams) : query(window.location.search);
            limitParams.set('ll_limit', value.dataset.value);
            limitParams.delete('page');
            var dropdown = limit.closest('[data-limited-view]');
            if (dropdown && dropdown.querySelector('[data-toggle]')) dropdown.querySelector('[data-toggle]').setAttribute('aria-expanded', 'false');
            this.render(limitParams, null, true);
        }
    };

    window.LenslottCollectionFacets = {
        handleRender: function (searchParams, event, updateURLHash) {
            if (!controller || !controller.root.isConnected) return false;
            if (!controller.ready) {
                if (!controller.initializing) return false;
                controller.queuedRender = { searchParams: query(searchParams).toString(), event: event, updateHistory: updateURLHash };
                return true;
            }
            controller.render(searchParams, event, updateURLHash);
            return true;
        },
        getState: function () {
            return controller ? { ready: controller.ready, collection: controller.collection, searchParams: controller.lastParams.toString(), cachedQueries: controller.cache.size } : null;
        }
    };

    function initialize() {
        var root = document.querySelector(ROOT_SELECTOR);
        if (root && !controller) controller = new FacetController(root);
    }

    // Head-level deferred assets can execute before the collection's own
    // deferred custom-element script. DOMContentLoaded waits for both scripts.
    if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
}());
